import { Auth } from "../libs/utilities/helpers/Auth";
import { getAutoCoderModel } from "../pages/Auto-Coder/autoCoderConfig";
import { AutoCoderUploadFolderModalAPI } from "./AutoCoderUploadFolderModalApi";

const CONVERSATION_PATH = "/autocoder/conversation";
const ERROR_MESSAGES = {
  ERR0001: "Something went wrong — please try again.",
  ERR0002: "Your session has expired — please sign in again.",
  ERR0003: "Authentication token missing — please sign in again.",
  ERR0013: "Model unavailable — please try again.",
  ERR0016: "Invalid model or settings — please check Generation settings.",
  ERR0018: "Please select a valid file.",
  ERR0019: "Please enter a message.",
  ERR0035: "One or more files could not be read — try re-uploading.",
  ERR0044: "Request timed out — try a shorter question.",
  ERR0045: "Please select at least one file.",
  ERR0046: "One or more files are unavailable or access denied.",
  ERR0047: "Combined file size exceeds 5MB limit.",
  ERR0049: "Sorry, looks like there is some issue with **${label}** right now, or we have hit a snag.Could you please retry again in some time or try a different model?",
};

export const getEmptyModelResponseMessage = (modelId) => {
  const model = getAutoCoderModel(modelId);
  const label = model?.label || modelId || "the selected model";
  return (
    `Sorry, looks like there is some issue with ${label} right now, or we have hit a snag. ` +
    "Could you please retry again in some time or try a different model?"
  );
};

export const getAutoCoderErrorMessage = (payload, fallback = "Something went wrong!") => {
  if (!payload) return fallback;

  const code = payload.statusCode;

  if (code === "ERR0049" && payload.message) {
    return "Sorry, looks like there is some issue with the selected model right now,\n or we have hit a snag.Could you please retry again in some time or try a different model?";
  }
  if (code && ERROR_MESSAGES[code]) {
    return ERROR_MESSAGES[code];
  }

  if (payload.message && typeof payload.message === "string") {
    return payload.message;
  }

  return fallback;
};

const isErrorStatusCode = (statusCode) =>
  typeof statusCode === "string" && statusCode.startsWith("ERR");

const buildAutoCoderUrl = (path) => {
  const baseURL = (process.env.REACT_APP_BASE_URL_AUTOCODER || "").replace(/\/$/, "");
  const endpointPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseURL}/api/v1.0${endpointPath}`;
};

const buildAuthHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const token = Auth.getToken();
  const user = Auth.getUser();

  if (token) {
    headers.Authorization = token;
  }
  if (user) {
    headers.txn_id = user.Txnid;
    headers.user_id = user.UserId;
    headers.source = "Galaxy";
  }

  return headers;
};

const normalizeConversationResponse = (payload, clientLatencyMs, fallbackModelId) => {
  if (isErrorStatusCode(payload?.statusCode)) {
    const err = new Error(getAutoCoderErrorMessage(payload));
    err.payload = payload;
    err.status = 400;
    throw err;
  }

  const data = payload?.data ?? payload ?? {};

  const answer =
    data.response ??
    data.answer ??
    payload?.response ??
    payload?.answer ??
    "";

  const promptTokens = data.prompt_tokens ?? payload?.prompt_tokens;
  const completionTokens = data.completion_tokens ?? payload?.completion_tokens;
  const totalTokens =
    data.total_tokens ??
    payload?.total_tokens ??
    (promptTokens != null && completionTokens != null
      ? promptTokens + completionTokens
      : undefined);

  const timeTakenMs = data.time_taken_ms ?? payload?.time_taken_ms;

  return {
    answer: typeof answer === "string" ? answer : "",
    meta: data.meta ?? data.references ?? [],
    model: data.model ?? payload?.model ?? fallbackModelId,
    requestId: data.request_id ?? payload?.request_id,
    statusCode: data.status_code ?? payload?.status_code,
    timeTakenMs,
    latencyMs: timeTakenMs ?? clientLatencyMs,
    promptTokens,
    completionTokens,
    totalTokens,
    conversationId: data.conversation_id ?? data.request_id ?? payload?.request_id,
    raw: payload,
  };
};

export const sendAutoCoderConversation = async ({ body, signal, fallbackModelId }) => {
  const startedAt = performance.now();
  const url = buildAutoCoderUrl(CONVERSATION_PATH);

  const response = await fetch(url, {
    method: "POST",
    headers: buildAuthHeaders(),
    body: JSON.stringify(body),
    signal,
  });

  const clientLatencyMs = Math.round(performance.now() - startedAt);

  let payload;
  try {
    payload = await response.json();
  } catch {
    payload = { message: response.statusText || "Request failed" };
  }

  if (!response.ok) {
    const error = new Error(getAutoCoderErrorMessage(payload, "Auto Coder request failed"));
    error.payload = payload;
    error.status = response.status;
    throw error;
  }

  return normalizeConversationResponse(payload, clientLatencyMs, fallbackModelId);
};
